<template>
  <div id="app">
    <LoadingSpinner v-if="loading" />
    <template v-else>
      <notifications group="alert" position="top center" classes="vue-notification alert-notification" />

      <div v-if="$route.meta.layout === 'auth'" class="wrap-bg">
        <div class="app-auth">
          <router-view />
        </div>
      </div>

      <div v-else>
        <div class="app-body-wrap">
          <Popup v-if="popupProps" v-bind="popupProps" @closePopup="answer => popupResolve(answer)" />
          <header class="app-header">
            <Link to="/" class="logo">
              <img src="./assets/logo.svg" alt="logo" />
            </Link>
            <MainNav v-if="companyUser" @logoutUser="logout" />
          </header>
          <section class="app-body">
            <router-view />
          </section>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import '@fontsource/source-sans-pro/400.css'
import '@fontsource/source-sans-pro/600.css'
import '@fontsource/source-sans-pro/700.css'
import 'material-icons/iconfont/filled.css'
import { computed } from 'vue'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'

import LoadingSpinner from '@/components/LoadingSpinner'
import MainNav from '@/components/MainNav'
import Popup from '@/components/Popup'

import authApi from '@/api/auth'
import { get } from '@/api/base'
import companyUserApi from '@/api/companyUser'
import { getRedirectPath, isAuthorized } from '@/router'
import { adLogout } from '@/utils/active_directory_helper'
import { mapSettings } from '@/utils/settings'

export default {
  data: () => ({
    companyUser: null,
    loading: true,
    popupProps: null,
    popupResolve: null,
    user: null,
  }),
  components: {
    LoadingSpinner,
    MainNav,
    Popup,
  },
  computed: {
    ...mapSettings(['companyUuid', 'userLanguage']),
  },
  methods: {
    async getUser() {
      const { success, data } = await get({ path: 'user' })

      if (!success) return

      this.user = data.user

      // We currently don't support Polish and Ukrainian languages in binity-app but these are
      // supported languages in binity-driver, binity-tour and binity-order.
      // Right now we only support English and German (fallback for everything else):
      const locale = this.user.language === 'en' ? 'en' : 'de'
      this.$i18n.locale = locale
      this.userLanguage = locale
      document.documentElement.lang = locale
    },
    async getCompanyUser() {
      // if user does not have access to the company, redirect to first company
      if (!this.user.companies.find(c => c.uuid === this.companyUuid)) {
        this.companyUuid = this.user.companies[0]?.uuid
      }

      if (!this.companyUuid) return // user does not have access to binity-app in any company

      this.companyUser = await companyUserApi.get(this.companyUuid)
    },
    async logout() {
      await authApi.signOut()
      await adLogout()

      this.$router.push({ name: 'auth_sign_in' })
    },
    showPopup(popupProps) {
      const popupPromise = new Promise(resolve => {
        this.popupResolve = resolve
        this.popupProps = popupProps
      }).finally(() => {
        this.popupProps = null
      })
      return popupPromise
    },
  },
  async created() {
    // this.$route is empty here so we need to resolve it ourself
    const route = this.$router.resolve(window.location.pathname).route

    await this.getUser()
    // check if user is logged in

    if (this.user == null) {
      // no user, thats fine on some pages and on others we have to redirect
      if (route.meta.requiresAuth !== false) this.$router.push({ name: 'auth_sign_in' })
    } else {
      await this.getCompanyUser()

      // check if user is authorized on that page for that company
      if (!isAuthorized(this.companyUser, route)) {
        console.log('unauthorized')
        this.$router.push(getRedirectPath(this.companyUser, route.fullPath))
      }
    }

    this.loading = false
  },
  provide() {
    return {
      showPopup: this.showPopup.bind(this),
      companyUser: computed(() => this.companyUser),
    }
  },
}
</script>

<style lang="scss">
@import './assets/css/normalize.css';
@import './assets/css/skadi.scss';
@import './assets/css/style.scss';
@import './assets/css/form.scss';
</style>

<style lang="scss" scoped>
.app-header {
  width: 1400px;
  height: var(--nav-height);
  border-right: 1px solid #314053;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  border-left: 1px solid #314053;
  z-index: 99999;
  background: #314053;
  position: fixed;
  top: 0;
  display: flex;
  margin-left: -1px;

  .logo {
    /* 
    Background image is for PDF/printing.
    We pre-load it here in order to bypass any timing problems
    when starting the printing dialog
    */
    background-image: url('./assets/logo_i.svg');
    margin: 21px 20px 14px 20px;
    width: 98px;
    align-self: center;

    img {
      height: 35px;
      display: block;
    }
  }

  @media only screen and (max-width: 1280px) {
    .logo {
      margin: 0.375rem 0.5rem 0 1rem;
    }
  }
}

@media only screen and (max-width: 1400px) {
  .app-header {
    width: 100%;
  }
}
</style>
